import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  mergeGuestSaves, mergeGuestDrafts, resolveGuestReviewConflict, hasPendingGuestContent,
  removeGuestDraft, unquarantineGuestDraft,
} from '../hooks/useGuestSaves';
import { reportEvent } from '../utils/telemetry';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  // Set only when logout() is triggered by a detected session expiry (as
  // opposed to an explicit user-initiated logout), so a banner can tell the
  // user why they were signed out instead of failing silently.
  const [sessionMessage, setSessionMessage] = useState(null);
  // Guest review drafts that collided with an existing account review on login,
  // surfaced for the user to resolve (keep theirs vs use the draft). See #13.
  const [guestReviewConflicts, setGuestReviewConflicts] = useState([]);
  // Guest drafts the server rejected for good, surfaced after login so the user
  // can try again or discard them instead of them failing silently (#227).
  const [guestDraftFailures, setGuestDraftFailures] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const userIdFromToken = (accessToken) => {
    try {
      return JSON.parse(atob(accessToken.split('.')[1])).user_id;
    } catch (e) {
      return null; // best-effort token decode
    }
  };

  // Check if token is expired by decoding it
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired (with 5 second buffer)
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  };

  useEffect(() => {
    // Check if user is logged in on mount
    if (token) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        logout('Your session has expired. Please sign in again.');
      } else {
        // Fetch full user profile from API
        fetchUserProfile();
        checkTermsStatus(token);
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // If can't fetch profile, decode from token as fallback
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ email: payload.sub, id: payload.user_id });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Fallback to token decode
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ email: payload.sub, id: payload.user_id });
      } catch (decodeError) {
        console.error('Error decoding token:', decodeError);
        logout('Your session has expired. Please sign in again.');
      }
    }
  };

  // Terms of Use acceptance (App Store / UGC compliance — same terms on all surfaces)
  const checkTermsStatus = async (authToken) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/terms/status`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (response.ok) {
        const status = await response.json();
        setNeedsTermsAcceptance(!status.accepted);
      }
    } catch (error) {
      console.error('Error checking terms status:', error);
    }
  };

  const acceptCurrentTerms = async (authToken = null) => {
    const tok = authToken || token;
    if (!tok) return false;
    try {
      const response = await fetch(`${API_BASE_URL}/users/terms/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tok}` },
      });
      if (response.ok) {
        setNeedsTermsAcceptance(false);
        return true;
      }
    } catch (error) {
      console.error('Error accepting terms:', error);
    }
    return false;
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();

        // Check if token expired
        if (error.detail && error.detail.includes('expired')) {
          logout();
          throw new Error('Session expired. Please login again.');
        }

        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('token', data.access_token);

      // Fetch full user profile from API
      try {
        const profileResponse = await fetch(`${API_BASE_URL}/users/me`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`,
          },
        });

        if (profileResponse.ok) {
          const userData = await profileResponse.json();
          setUser(userData);
        } else {
          // Fallback to token decode if profile fetch fails
          const payload = JSON.parse(atob(data.access_token.split('.')[1]));
          setUser({ email: payload.sub, id: payload.user_id });
        }
      } catch (error) {
        console.error('Error fetching user profile after login:', error);
        // Fallback to token decode
        const payload = JSON.parse(atob(data.access_token.split('.')[1]));
        setUser({ email: payload.sub, id: payload.user_id });
      }

      // Existing users who predate the terms get a one-time acceptance prompt
      checkTermsStatus(data.access_token);

      // Whether this login is a guest *converting* — captured before the merge
      // clears the local store, so we can fire the signal once (issue #79 P3).
      const wasGuestConversion = hasPendingGuestContent();

      // Fold any "saved without an account" guest favorites/wishlist into the
      // now-authenticated account, then clear localStorage (issue #13). Never
      // let a merge failure break login — guest items are kept locally on error.
      try {
        await mergeGuestSaves(API_BASE_URL, data.access_token);
      } catch (error) {
        console.error('Error merging guest saves after login:', error);
      }

      // Post guest review/visit drafts. A review that collides with an existing
      // account review comes back as a conflict for the user to resolve.
      try {
        const { conflicts, failed } = await mergeGuestDrafts(
          API_BASE_URL, data.access_token, userIdFromToken(data.access_token));
        if (conflicts && conflicts.length) setGuestReviewConflicts(conflicts);
        setGuestDraftFailures(failed || []);
      } catch (error) {
        console.error('Error merging guest drafts after login:', error);
      }

      // A guest with pending content just signed in — the conversion we care
      // about for the #13 bet. Aggregate signal only, fired once.
      if (wasGuestConversion) reportEvent('guest_conversion');

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      // After registration, automatically login
      const loginResult = await login(userData.email, userData.password);

      // User accepted the terms via the register form checkbox — record it
      if (loginResult.success) {
        await acceptCurrentTerms(localStorage.getItem('token'));
      }

      return loginResult;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // `message` is set when logout is triggered by a detected session expiry,
  // so the banner can explain why — left null for explicit user logouts.
  const logout = (message = null) => {
    setUser(null);
    setToken(null);
    setNeedsTermsAcceptance(false);
    localStorage.removeItem('token');
    if (message) setSessionMessage(message);
  };

  // Helper function to make authenticated API calls with automatic token expiry handling
  const authenticatedFetch = async (url, options = {}) => {
    // Check if token is expired before making request
    if (isTokenExpired(token)) {
      logout('Your session has expired. Please sign in again.');
      throw new Error('Session expired. Please login again.');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(url, { ...options, headers });

    // If we get a 401, check if it's due to token expiration
    if (response.status === 401) {
      const error = await response.json();
      if (error.detail && error.detail.includes('expired')) {
        logout('Your session has expired. Please sign in again.');
        throw new Error('Session expired. Please login again.');
      }
    }

    return response;
  };

  // Resolve one guest-review merge conflict — keep the account's review, or PUT
  // the draft over it — then drop it from the queue. See #13.
  const resolveReviewConflict = async (conflict, choice) => {
    try {
      await resolveGuestReviewConflict(API_BASE_URL, token, conflict, choice);
    } catch (error) {
      console.error('Error resolving guest review conflict:', error);
    }
    setGuestReviewConflicts((prev) => prev.filter((c) => c !== conflict));
  };

  // A guest draft that couldn't post (#227). "Try again" lifts the quarantine
  // and runs the merge once more; whatever still fails comes straight back.
  // "Discard" is the only way it's deleted. "Not now" just closes the dialog:
  // the draft stays quarantined and shows again at the next sign-in.
  const retryFailedDraft = async (failure) => {
    unquarantineGuestDraft(failure.kind, failure.draft.draft_id);
    try {
      const { conflicts, failed } = await mergeGuestDrafts(API_BASE_URL, token, userIdFromToken(token));
      if (conflicts && conflicts.length) setGuestReviewConflicts(conflicts);
      setGuestDraftFailures(failed || []);
    } catch (error) {
      console.error('Error retrying guest draft:', error);
    }
  };

  const discardFailedDraft = (failure) => {
    removeGuestDraft(failure.kind, failure.draft.draft_id);
    setGuestDraftFailures((prev) => prev.filter((f) => f !== failure));
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token,
    authenticatedFetch, // Export for use in components
    isTokenExpired,
    needsTermsAcceptance,
    acceptCurrentTerms,
    sessionMessage,
    clearSessionMessage: () => setSessionMessage(null),
    guestReviewConflicts,
    resolveReviewConflict,
    guestDraftFailures,
    retryFailedDraft,
    discardFailedDraft,
    dismissDraftFailures: () => setGuestDraftFailures([]),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
